<template>
    <v-container>
        <v-card style="min-width: 1400px">
            <v-card-title class="d-flex justify-start align-center">
                <span class="mr-2 text-h8">Exited Trades</span>
                <v-btn color="secondary" icon variant="text" size="large" title="Refresh" class="ml-2" @click="fetchTrades">
                    <v-icon size="32" class="font-weight-bold">mdi-refresh</v-icon>
                </v-btn>
            </v-card-title>
    
            <v-card-text>
                <!-- Filters -->
                <v-row class="mb-4" align="center" justify="start">
                    <v-col cols="12" sm="3">
                        <v-text-field v-model="fromDate" label="From Date" type="date" density="compact" />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field v-model="toDate" label="To Date" type="date" density="compact" />
                    </v-col>
                    <v-col cols="12" sm="3" class="d-flex align-end">
                        <v-btn color="primary" @click="fetchTrades">Fetch Trades</v-btn>
                    </v-col>
                </v-row>
    
                <v-progress-linear v-if="loading" indeterminate color="primary" />
                <!-- Total PnL Top Left -->
                <v-row class="mb-2" justify="start">
                    <v-col cols="auto">
                        <div class="d-flex align-center">
                            <strong class="mr-2">Total PnL:</strong>
                            <span :class="totalPnL >= 0 ? 'pnl-green' : 'pnl-red'">
                                 {{ formatCurrency(totalPnL.toFixed(2) ) }}
                            </span>
                        </div>
                    </v-col>
                </v-row>
                <!-- Exited Trades Table -->
                <v-data-table :headers="headers" :items="exitedTrades" :items-per-page="itemsPerPage"
                    :server-items-length="totalCount" class="elevation-1">
                    <template #item="{ item }">
                        <tr>
                            <td>{{ item.strategyName }}</td>
                            <td>{{ item.strategyType }}</td>
                            <td :class="item.pnl >= 0 ? 'pnl-green' : 'pnl-red'">{{ formatCurrency(item.pnl.toFixed(2) ) }}</td>
                            <td>{{ item.totalDebit }}</td>
                            <td>{{ item.totalCredit }}</td>
                            <td>{{ item.totalCreditDebit }}</td>
                            <td>{{ item.startDate }}</td>
                            <td>{{ item.endDate }}</td>
                            <td>
                                <button class="view-link" @click="openPositions(item.positionInfoList)">View</button>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
                <!-- Pagination Footer -->
                <v-row align="center" justify="space-between" class="mt-4">
                    <v-col cols="auto">
                        <span class="font-weight-medium">Page {{ page }} of {{ totalPages }}</span>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    
        <!-- Modal for position info -->
        <v-dialog v-model="showModal" max-width="1200">
            <v-card>
                <v-card-title class="text-h6">Position Info</v-card-title>
                <v-card-text>
                    <v-data-table :headers="positionHeaders" :items="selectedPositions" dense>
                        <template #item="{ item }">
                            <tr>
                                <td>{{ item.stock_code }}</td>
                                <td>{{ item.action }}</td>
                                <td>{{ item.product_type }}</td>
                                <td>{{ item.strike_price }}</td>
                                <td>{{ item.right }}</td>
                                <td>{{ item.quantity }}</td>
                                <td>{{ item.average_price }}</td>
                                <td>{{ item.exit_price }}</td>
                                <td :class="item.position_pnl >= 0 ? 'pnl-green' : 'pnl-red'">{{ item.position_pnl }}</td>
                                <td>
                                    <div class="d-flex align-center">
                                        <v-img :src="item.application === 'BREEZE' ? '/icici.png' : '/zerodha.png'"
                                            :alt="item.application" :max-width="item.application === 'BREEZE' ? 50 : 25"
                                            :max-height="item.application === 'BREEZE' ? 50 : 25" cover />
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
    
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn text @click="showModal = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>
  
  <script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

const fromDate = ref('2025-07-31')
const toDate = ref(new Date().toISOString().slice(0, 10))
const page = ref(1)
const itemsPerPage = 50
const loading = ref(false)
const exitedTrades = ref([])
const totalCount = ref(0)

const showModal = ref(false)
const selectedPositions = ref([])
const totalPnL = computed(() =>
  exitedTrades.value.reduce((sum, trade) => sum + (parseFloat(trade.pnl) || 0), 0)
)
const headers = [
  { title: 'Strategy', key: 'strategyName' },
  { title: 'Type', key: 'strategyType' },
  { title: 'PnL', key: 'pnl' },
  { title: 'Debit', key: 'totalDebit' },
  { title: 'Credit', key: 'totalCredit' },
  { title: 'Net', key: 'totalCreditDebit' },
  { title: 'Start', key: 'startDate' },
  { title: 'End', key: 'endDate' },
  { title: 'Positions', key: 'positions', sortable: false }
]

const positionHeaders = [
  { title: 'Stock', key: 'stock_code' },
  { title: 'Action', key: 'action' },
  { title: 'Type', key: 'product_type' },
  { title: 'Strike', key: 'strike_price' },
  { title: 'Right', key: 'right' },
  { title: 'Qty', key: 'quantity' },
  { title: 'Avg Price', key: 'average_price' },
  { title: 'Exit Price', key: 'exit_price' },
  { title: 'PnL', key: 'position_pnl' },
  { title: 'App', key: 'application' }
]

const totalPages = computed(() => Math.ceil(totalCount.value / itemsPerPage))

// Format currency function
function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

function openPositions(list) {
  selectedPositions.value = list
  showModal.value = true
}

async function fetchTrades() {
  loading.value = true
  try {
    const res = await store.dispatch('trades/getExitedTradeDetailList', {
      fromDate: fromDate.value,
      toDate: toDate.value,
      offset: (page.value - 1) * itemsPerPage,
      count: itemsPerPage
    })
    exitedTrades.value = res.exitedTradeDetailList || []
    totalCount.value = exitedTrades.value.length
  } catch (err) {
    console.error('Failed to fetch exited trades:', err)
  } finally {
    loading.value = false
  }
}

onMounted(fetchTrades)
  </script>
  <style scoped>
:deep(th) {
    font-weight: bold !important;
    font-size: 14px !important;
    background-color: #fbeee6 !important;
}

.pnl-green {
    color: #4caf50;
    font-weight: 600;
}

.pnl-red {
    color: #f44336;
    font-weight: 600;
}

.view-link {
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
    background: none;
    border: none;
    font-weight: 500;
    font-size: 14px;
}

:deep(.v-table) {
    border-top: 1px solid #ddd;
}
</style>
  